import type { FieldPath, FieldValues, UseFormReturn } from 'react-hook-form';

import { Maybe } from 'types/UtilityTypes';

import { getRequiredAccordionSections } from './accordionSectionMapping';
import { formatFieldLabel } from './fieldLabelMaps';
import { formatFormErrors } from './formatFormErrors';

/** Default cap on bullet lines in the "Invalid fields" validation toast. */
export const DEFAULT_MAX_VALIDATION_TOAST_LABELS = 8;

/**
 * Toast function type from useToast hook
 */
type ToastFunction = (options: {
  description: string;
  variant?: Maybe<'default' | 'destructive' | 'success' | 'info' | 'warning'>;
  [key: string]: any;
}) => any;

/**
 * Options for the form error handler.
 */
export interface FormErrorHandlerParams {
  /** Optional TMS name for TMS-specific field label mappings */
  tmsName?: string;
  /** Maximum number of field labels to show in the toast message (default: 8) */
  maxLabelsToShow?: number;
  /** Array of currently active/expanded accordion tab names */
  activeTabs: string[];
  /** Function to update the active accordion tabs */
  setActiveTabs: (tabs: string[]) => void;
  /** Toast function to display error messages (required when showToast is true) */
  toast: ToastFunction;
  /**
   * Called after toast, accordion updates, and scroll (e.g. `setIsLoading(false)`).
   * Runs for both normal and empty-error fallback paths.
   */
  afterErrorHandled?: () => void;
  // McLeod route length so toast labels match Pickup / Stop N / Consignee cards.
  routeStopCount?: number;
  /**
   * When false, expand accordion sections with errors but skip the destructive
   * toast. AI-suggestion hydration (field errors only).
   * Default true (submit / onInvalid behavior).
   */
  showToast?: boolean;
  /**
   * When false, skip scrolling to the first invalid field. Default true.
   */
  scrollToField?: boolean;
}

export type ManualFieldError<T extends FieldValues> = {
  path: FieldPath<T>;
  message: string;
};

/**
 * Shared options for TMS load-building forms that call {@link applyManualValidationErrors}
 * after submit (accordion + toast + optional loading reset).
 */
export function loadBuildingManualValidationDisplayOptions(
  options: Pick<
    FormErrorHandlerParams,
    'tmsName' | 'activeTabs' | 'setActiveTabs' | 'toast' | 'routeStopCount'
  > & { afterErrorHandled: () => void }
): FormErrorHandlerParams {
  return {
    ...options,
    maxLabelsToShow: DEFAULT_MAX_VALIDATION_TOAST_LABELS,
  };
}

function buildNestedErrorsFromFailures(
  failures: Array<{ path: string; message: string }>
): Record<string, unknown> {
  const root: Record<string, unknown> = {};
  for (const { path, message } of failures) {
    const parts = path.split('.');
    let cur: Record<string, unknown> = root;
    for (let i = 0; i < parts.length - 1; i++) {
      const p = parts[i];
      if (!cur[p] || typeof cur[p] !== 'object') {
        cur[p] = {};
      }
      cur = cur[p] as Record<string, unknown>;
    }
    cur[parts[parts.length - 1]] = { type: 'manual', message };
  }
  return root;
}

/**
 * Clears existing RHF errors, applies manual `setError` for each path, then runs the same
 * toast / accordion expansion / scroll behavior as {@link handleFormErrors}.
 */
export function applyManualValidationErrors<T extends FieldValues>(
  form: Pick<UseFormReturn<T>, 'setError' | 'clearErrors'>,
  failures: ManualFieldError<T>[],
  options: FormErrorHandlerParams
): void {
  form.clearErrors();
  for (const { path, message } of failures) {
    form.setError(path, { type: 'manual', message });
  }
  const nested = buildNestedErrorsFromFailures(
    failures as Array<{ path: string; message: string }>
  );
  handleFormErrors(nested, options);
}

/**
 * Applies existing FE validation failures after an AI suggestion hydrates the
 * form: field-level red errors + accordion expand, without toast or auto-scroll.
 *
 * Merges into any errors already present (e.g. from `trigger()`), and still
 * expands accordion sections when only RHF rule errors exist.
 */
export function applySuggestionHydrationValidationErrors<T extends FieldValues>(
  form: Pick<UseFormReturn<T>, 'setError' | 'getFieldState'> &
    Partial<Pick<UseFormReturn<T>, 'getErrors' | 'formState'>>,
  failures: ManualFieldError<T>[],
  options: Pick<
    FormErrorHandlerParams,
    'tmsName' | 'activeTabs' | 'setActiveTabs' | 'routeStopCount'
  >
): void {
  const newlyApplied: Array<{ path: string; message: string }> = [];
  for (const { path, message } of failures) {
    // Keep a more specific RHF rule message when trigger() already flagged the path.
    if (form.getFieldState(path).error) {
      continue;
    }
    form.setError(path, { type: 'manual', message });
    newlyApplied.push({ path: String(path), message });
  }

  const rhfErrors =
    typeof form.getErrors === 'function'
      ? form.getErrors()
      : (form.formState?.errors ?? {});
  const existingInvalids = formatFormErrors(rhfErrors).map(
    ({ path, message }) => ({
      path,
      message: message ?? '',
    })
  );
  const existingPaths = new Set(existingInvalids.map((item) => item.path));
  const mergedFailures = [
    ...existingInvalids,
    ...newlyApplied.filter((item) => !existingPaths.has(item.path)),
  ];

  if (mergedFailures.length === 0) {
    return;
  }

  handleFormErrors(buildNestedErrorsFromFailures(mergedFailures), {
    ...options,
    toast: () => undefined,
    showToast: false,
    scrollToField: false,
  });
}

/**
 * Main error handler for form validation errors.
 *
 * This function orchestrates the entire error handling flow:
 * 1. Formats errors from react-hook-form into a flat structure
 * 2. Determines which accordion sections contain errors
 * 3. Auto-expands those sections
 * 4. Formats field labels for display
 * 5. Shows a toast with specific invalid fields
 * 6. Scrolls to the first invalid field
 *
 * @param errors - The errors object from react-hook-form's SubmitErrorHandler
 * @param options - Configuration options for error handling
 *
 * @example
 * const defaultOnInvalid: SubmitErrorHandler<NormalizedLoad> = async (errors) => {
 *   handleFormErrors(errors, {
 *     tmsName: TMS.Turvo,
 *     maxLabelsToShow: 8,
 *     activeTabs,
 *     setActiveTabs,
 *     toast,
 *   });
 * };
 */
export function handleFormErrors(
  errors: any,
  options: FormErrorHandlerParams
): void {
  const {
    tmsName,
    maxLabelsToShow = DEFAULT_MAX_VALIDATION_TOAST_LABELS,
    activeTabs,
    setActiveTabs,
    toast,
    afterErrorHandled,
    routeStopCount,
    showToast = true,
    scrollToField = true,
  } = options;

  // 1. Format errors into flat array
  const invalids = formatFormErrors(errors);

  // If no errors found, show generic message (submit path only)
  if (!invalids.length) {
    if (showToast) {
      toast({
        description: 'Some fields are invalid.',
        variant: 'destructive',
      });
    }
    afterErrorHandled?.();
    return;
  }

  // 2. Get accordion sections that need to be expanded
  const errorPaths = invalids.map((error) => error.path);
  const sectionsToExpand = getRequiredAccordionSections(errorPaths, tmsName);
  // 3. Auto-expand sections with errors (merge with currently active tabs)
  const updatedActiveTabs = Array.from(
    new Set([...activeTabs, ...sectionsToExpand])
  );
  setActiveTabs(updatedActiveTabs);

  // 4. Scroll to first invalid field
  if (scrollToField && invalids[0]) {
    scrollAndFocusField(invalids[0].path);
  }

  // 5. Format field labels and build toast description
  if (showToast) {
    const items = invalids
      .slice(0, maxLabelsToShow)
      .map(({ path, message }) => {
        const label = formatFieldLabel(path, tmsName, { routeStopCount });
        return message ? `${label} (${message})` : label;
      });

    const moreCount = invalids.length - items.length;

    const description =
      'Invalid fields:\n' +
      items.map((item) => `• ${item}`).join('\n') +
      (moreCount > 0 ? `\n• ...and ${moreCount} more` : '');

    // 6. Show toast
    toast({ description, variant: 'destructive' });
  }
  afterErrorHandled?.();
}

/**
 * Scrolls to and attempts to focus a form field by its field path.
 *
 * Tries multiple selector strategies:
 * - input[name="path"]
 * - select[name="path"]
 * - [data-name="path"] (for custom components like date-time pickers)
 * - label[name="path"] (for label-based components)
 *
 * @param path - The field path to scroll to (e.g., "customer.externalTMSID")
 *
 * @example
 * scrollAndFocusField('customer.externalTMSID')
 * scrollAndFocusField('rateData.customerLineItems.0.label')
 */
export function scrollAndFocusField(path: string): void {
  setTimeout(() => {
    const trySelectors = [`input[name="${path}"]`, `select[name="${path}"]`];

    let el: Maybe<HTMLElement> = document.querySelector(
      trySelectors.join(', ')
    ) as Maybe<HTMLElement>;

    // Date-time inputs and other custom components may expose a container with data-name
    if (!el) {
      el =
        (document.querySelector(
          `[data-name="${path}"]`
        ) as Maybe<HTMLElement>) ||
        (document.querySelector(`label[name="${path}"]`) as Maybe<HTMLElement>);
    }

    if (!el) return;

    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, 150);
}
